import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFormikContext, useField } from 'formik';
import PropTypes from 'prop-types';

import { ID, REFERRAL, FILES } from 'store/diagnoses';
import {
  FILES as MEDICAL_FILES,
  EXPAND,
  SUB_PATIENT,
  useMedicalHistory,
  useMedicalHistoryActions,
  useAlerts,
} from 'store/medicalHistory';

import MultiImage from 'components/MultiImage';

import { Container, Label } from './styles';

const toFileId = (file) => file?.[ID] || file;

const Documents = ({ readMode }) => {
  const { id: patientId } = useParams();
  const { setValues } = useFormikContext();
  const [{ value: selectedFiles = [] }] = useField(`${REFERRAL}.${FILES}`);
  const storedFiles = useMedicalHistory(MEDICAL_FILES);
  const { fetchCollectMedicalHistory } = useMedicalHistoryActions();
  const { action, success } = useAlerts(fetchCollectMedicalHistory);
  const files = useMemo(() => success?.[MEDICAL_FILES] || storedFiles || [], [storedFiles, success]);
  const availableIds = useMemo(() => new Set(files.map((item) => item[ID])), [files]);
  const selectedIds = useMemo(() => {
    const ids = selectedFiles.map(toFileId).filter(Boolean);
    const hasSource = Boolean(success) || Array.isArray(storedFiles);

    return hasSource ? ids.filter((fileId) => availableIds.has(fileId)) : ids;
  }, [availableIds, selectedFiles, storedFiles, success]);

  useEffect(() => {
    if (patientId) {
      action({ [SUB_PATIENT]: patientId, [EXPAND]: [MEDICAL_FILES] });
    }
  }, [action, patientId]);

  useEffect(() => {
    const hasSource = Boolean(success) || Array.isArray(storedFiles);

    if (!hasSource || selectedIds.length === selectedFiles.length) return;

    setValues(($) => ({
      ...$,
      [REFERRAL]: { ...$[REFERRAL], [FILES]: selectedIds },
    }));
  }, [selectedFiles.length, selectedIds, setValues, storedFiles, success]);

  const onToggleSelect = useCallback(
    (fileId) => {
      setValues(($) => {
        const current = ($[REFERRAL]?.[FILES] || []).map(toFileId);
        const next = current.includes(fileId) ? current.filter((id) => id !== fileId) : [...current, fileId];

        return {
          ...$,
          [REFERRAL]: { ...$[REFERRAL], [FILES]: next },
        };
      });
    },
    [setValues]
  );

  return (
    <Container>
      <Label>Wählen Sie die Dokumente aus, die Sie mit der ausgewählten Klinik teilen möchten</Label>
      <MultiImage
        values={files}
        readMode={readMode}
        selectable
        selectedIds={selectedIds}
        onToggleSelect={readMode ? null : onToggleSelect}
        emptyState="box"
      />
    </Container>
  );
};

Documents.propTypes = { readMode: PropTypes.bool.isRequired };

export default Documents;
